/**
 * Editor del convenio seleccionado.
 *
 * Componente presentacional — el estado de los campos, el guardado y el
 * cálculo de los avisos legales viven en `PolicyAdmin` (orquestador), que es
 * quien controla el `useEffect` de resync (clave: solo al cambiar de
 * convenio seleccionado, nunca en cada tecla). Aquí solo se pinta el
 * formulario y se delega en los callbacks del padre.
 *
 * `legalOvertimeCap === null` significa "esa jurisdicción no fija tope", por
 * eso el texto es "sin tope legal" y nunca se sustituye por `Infinity`.
 */
import { WorkPolicyView } from '../../../services/api/workPolicies';
import { Icon } from '../../ui/Icon';

interface PolicyEditorProps {
  policy: WorkPolicyView;

  name: string;
  onNameChange: (value: string) => void;

  overtime: string;
  onOvertimeChange: (rawValue: string) => void;
  legalOvertimeCap: number | null;
  over: boolean;
  overtimeMsg: string;

  vacation: string;
  onVacationChange: (rawValue: string) => void;
  vUnder: boolean;

  nightStart: string;
  onNightStartChange: (value: string) => void;
  nightEnd: string;
  onNightEndChange: (value: string) => void;

  lunchAuto: boolean;
  onToggleLunchAuto: () => void;
  lunchMins: string;
  onLunchMinsChange: (rawValue: string) => void;

  saving: boolean;
  savedMsg: string | null;
  onSave: () => void;
}

export function PolicyEditor({
  policy,
  name,
  onNameChange,
  overtime,
  onOvertimeChange,
  legalOvertimeCap,
  over,
  overtimeMsg,
  vacation,
  onVacationChange,
  vUnder,
  nightStart,
  onNightStartChange,
  nightEnd,
  onNightEndChange,
  lunchAuto,
  onToggleLunchAuto,
  lunchMins,
  onLunchMinsChange,
  saving,
  savedMsg,
  onSave,
}: PolicyEditorProps) {
  return (
    <>
      <div className="flex gap-3 items-start rounded-xl bg-brand-soft border border-line-strong px-4.5 px-5 py-3.5">
        <Icon name="shield" size={22} className="text-brand shrink-0 mt-0.5" strokeWidth={1.8} />
        <p className="text-[14px] text-ink-2">
          <strong className="text-brand">El convenio solo puede apretar la ley, nunca aflojarla.</strong>{' '}
          Los mínimos y máximos legales están fijados; tú puedes ser más protector, no menos.
        </p>
      </div>

      {/* Nombre + jornada anual */}
      <div className="card-surface p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <div>
            <label className="label">Nombre del convenio</label>
            <input className="field" value={name} onChange={(e) => onNameChange(e.target.value)} />
          </div>
          <div>
            {/* La jornada anual NO es un control: se guarda, pero NINGÚN
                cálculo lo consume todavía (lo declara el propio endpoint) y
                el backend no valida la suma anual contra ella. Renderizarla
                como campo editable rotulado con el art. 34.1 ET la vendía
                como un control vivo de distribución irregular de jornada que
                no existe — y su eco se materializaba en `rules` en cada
                guardado (ronda 4, r2b07[1]). Dato informativo, claramente
                inerte. */}
            <label className="label">Jornada anual (informativa)</label>
            <p className="font-display font-extrabold text-[26px] mt-1.5">
              {policy.annual_hours.toLocaleString('es-ES')}{' '}
              <span className="text-[15px] text-ink-2 font-semibold">h/año</span>
            </p>
            <p className="text-[12px] text-ink-3 mt-1.5 max-w-[34ch]">
              Se guarda, pero ningún cálculo la consume todavía: no controla
              topes ni jornadas.
            </p>
          </div>
        </div>
      </div>

      {/* Horas extra: el clamp */}
      <div className="card-surface p-5 md:p-6">
        <div className="flex justify-between items-start flex-wrap gap-2.5">
          <div>
            <p className="font-bold text-[16px]">Horas extraordinarias / año</p>
            <p className="text-[13.5px] text-ink-2">
              Tope legal: {legalOvertimeCap ?? 'sin tope legal'} h, proporcional a la jornada (art. 35.2 ET).
            </p>
          </div>
          <span className="font-mono text-[12px] text-ink-3 border border-line-strong rounded-lg px-2.5 py-1">
            art. 35.2 ET
          </span>
        </div>

        <div
          className="flex items-stretch rounded-xl overflow-hidden bg-canvas mt-4 max-w-[280px] transition-colors"
          style={{ border: `1.5px solid ${over ? 'var(--signal)' : 'var(--line-strong)'}` }}
        >
          <input
            inputMode="numeric"
            value={overtime}
            onChange={(e) => onOvertimeChange(e.target.value)}
            className="flex-1 min-w-0 bg-transparent font-mono text-[26px] font-semibold px-4 py-3 outline-none"
            aria-describedby="ot-msg"
          />
          <span className="grid place-items-center px-4 font-mono text-[13px] text-ink-3 border-l border-line">
            h/año
          </span>
        </div>
        <p
          id="ot-msg"
          role="status"
          className="mt-3 flex items-center gap-2.5 font-mono text-[13px]"
          style={{ color: over ? 'var(--signal)' : 'var(--brand)' }}
        >
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: 'currentColor' }} />
          {overtimeMsg}
        </p>
      </div>

      {/* Vacaciones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        <div className="card-surface p-5">
          <p className="font-semibold text-[15px]">Vacaciones anuales</p>
          <div
            className="flex items-stretch rounded-xl overflow-hidden bg-canvas mt-3 max-w-[220px]"
            style={{ border: `1.5px solid ${vUnder ? 'var(--signal)' : 'var(--line-strong)'}` }}
          >
            <input
              inputMode="numeric"
              value={vacation}
              onChange={(e) => onVacationChange(e.target.value)}
              className="flex-1 min-w-0 bg-transparent font-mono text-[26px] font-semibold px-4 py-2.5 outline-none"
            />
            <span className="grid place-items-center px-3.5 font-mono text-[12px] text-ink-3 border-l border-line">
              días nat.
            </span>
          </div>
          <p className="font-mono text-[11.5px] mt-2.5" style={{ color: vUnder ? 'var(--signal)' : 'var(--ink-3)' }}>
            {vUnder
              ? `El mínimo son ${policy.legal_min_vacation_days} días naturales (art. 38). Al guardar se ajusta.`
              : `art. 38 · no puedes bajar de ${policy.legal_min_vacation_days}`}
          </p>
        </div>
        <div className="card-surface p-5">
          <p className="font-semibold text-[15px]">Noches seguidas máx.</p>
          {policy.legal_max_consecutive_nights !== null ? (
            <p className="font-display font-extrabold text-[26px] mt-1.5">
              {policy.legal_max_consecutive_nights}{' '}
              <span className="text-[15px] text-ink-2 font-semibold">noches</span>
            </p>
          ) : (
            <p className="font-display font-extrabold text-[20px] mt-1.5">Sin tope legal</p>
          )}
          <p className="font-mono text-[11.5px] text-ink-3 mt-1">
            art. 36.3 · fijado por la ley aplicable, no editable, salvo adscripción voluntaria
          </p>
        </div>
      </div>

      {/* Franja nocturna (parámetro del convenio) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        <div className="card-surface p-5">
          <p className="font-semibold text-[15px]">Franja nocturna</p>
          <p className="text-[13px] text-ink-2 mt-0.5">
            Horas que se cuentan como nocturnas. El convenio la fija; por defecto, la del Estatuto.
          </p>
          <div className="flex items-center gap-2.5 mt-3">
            <input
              type="time"
              value={nightStart}
              onChange={(e) => onNightStartChange(e.target.value)}
              className="field bg-canvas max-w-[130px] font-mono"
              aria-label="Inicio de la franja nocturna"
            />
            <span className="text-ink-3 font-mono text-[13px]">a</span>
            <input
              type="time"
              value={nightEnd}
              onChange={(e) => onNightEndChange(e.target.value)}
              className="field bg-canvas max-w-[130px] font-mono"
              aria-label="Fin de la franja nocturna"
            />
          </div>
          <p className="font-mono text-[11.5px] text-ink-3 mt-2.5">
            art. 36.1 · legal {policy.legal_night_start}–{policy.legal_night_end}
          </p>
        </div>

        {/* Comida automática */}
        <div className="card-surface p-5">
          <p className="font-semibold text-[15px]">Comida automática</p>
          <p className="text-[13px] text-ink-2 mt-0.5">
            Si el convenio descuenta la comida sin fichaje aparte.
          </p>
          <label className="flex items-center gap-3 mt-3 cursor-pointer">
            <button
              type="button"
              role="switch"
              aria-checked={lunchAuto}
              onClick={onToggleLunchAuto}
              className={`flex-none w-12 h-7 rounded-full relative transition-colors ${lunchAuto ? 'bg-brand' : 'bg-surface-3'}`}
            >
              <span
                className="absolute top-[3px] w-[22px] h-[22px] rounded-full bg-white shadow transition-all"
                style={{ left: lunchAuto ? 23 : 3 }}
              />
            </button>
            <span className="text-[14px] text-ink-2">{lunchAuto ? 'Activada' : 'Desactivada'}</span>
          </label>
          {lunchAuto && (
            <div className="flex items-center gap-2.5 mt-3">
              <input
                inputMode="numeric"
                value={lunchMins}
                onChange={(e) => onLunchMinsChange(e.target.value)}
                className="field bg-canvas max-w-[90px] font-mono text-center"
                aria-label="Minutos de comida"
              />
              <span className="text-ink-2 text-[13.5px]">min descontados tras 6 h trabajadas</span>
            </div>
          )}
        </div>
      </div>

      {savedMsg && (
        <p className="rounded-xl bg-brand-soft text-brand px-4 py-3 text-[14px]">{savedMsg}</p>
      )}
      <button onClick={onSave} disabled={saving} className="btn-primary h-12 max-w-[280px] text-[15px]">
        {saving ? 'Guardando…' : 'Guardar convenio'}
      </button>
    </>
  );
}
